import type { RadarCoverage, RadarRoute, RadarSamples, WeatherBounds } from './weather-layer-controller';

function lonLatToTile(lon: number, lat: number, z: number) {
  const safeLat = Math.max(-85.0511, Math.min(85.0511, lat));
  const n = 2 ** z;
  const latRad = safeLat * Math.PI / 180;
  return {
    x: Math.floor((((lon + 180) / 360) * n + n) % n),
    y: Math.floor((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n)
  };
}

function lonLatToTilePixel(lon: number, lat: number, z: number, tileSize = 512) {
  const safeLat = Math.max(-85.0511, Math.min(85.0511, lat));
  const n = 2 ** z;
  const latRad = safeLat * Math.PI / 180;
  const rawX = ((lon + 180) / 360) * n;
  const wrappedX = ((rawX % n) + n) % n;
  const rawY = (1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n;
  const x = Math.floor(wrappedX);
  const y = Math.max(0, Math.min(n - 1, Math.floor(rawY)));
  return {
    x, y,
    px: Math.max(0, Math.min(tileSize - 1, Math.floor((wrappedX - x) * tileSize))),
    py: Math.max(0, Math.min(tileSize - 1, Math.floor((rawY - y) * tileSize)))
  };
}

async function loadRadarImage(url: string): Promise<ImageData | null> {
  const response = await fetch(url);
  if (!response.ok) return null;
  const bitmap = await createImageBitmap(await response.blob());
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(bitmap, 0, 0);
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

function radarTileUrl(template: string, z: number, x: number, y: number) {
  return template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}

export async function sampleRadarRoutes(tileUrl: string, routes: RadarRoute[], z = 7): Promise<RadarSamples> {
  const tileCache = new Map<string, Promise<ImageData | null>>();
  const loadTile = (x: number, y: number) => {
    const key = `${z}/${x}/${y}`;
    if (!tileCache.has(key)) tileCache.set(key, loadRadarImage(radarTileUrl(tileUrl, z, x, y)));
    return tileCache.get(key)!;
  };
  const maxAlphaNear = (image: ImageData, px: number, py: number, radius = 10) => {
    let max = 0;
    for (let y = Math.max(0, py - radius); y <= Math.min(image.height - 1, py + radius); y += 1) {
      for (let x = Math.max(0, px - radius); x <= Math.min(image.width - 1, px + radius); x += 1) {
        max = Math.max(max, image.data[(y * image.width + x) * 4 + 3] / 255);
      }
    }
    return max;
  };
  const output: RadarSamples = {};
  await Promise.all(routes.map(async (route) => {
    const samples: Record<number, number> = {};
    const indices = route.coordinates.map((_, index) => index)
      .filter((index) => index % 6 === 0 || index === route.coordinates.length - 1);
    await Promise.all(indices.map(async (index) => {
      const coordinate = route.coordinates[index];
      const tile = lonLatToTilePixel(coordinate[0], coordinate[1], z);
      const image = await loadTile(tile.x, tile.y);
      if (!image) return;
      const alpha = maxAlphaNear(image, tile.px, tile.py);
      if (alpha > 0) samples[index] = alpha;
    }));
    output[route.route_num] = samples;
  }));
  return output;
}

export async function measureRadarCoverage(
  tileUrl: string,
  bounds: WeatherBounds,
  nearestDbz: (red: number, green: number, blue: number, alpha: number) => number,
  z = 7
): Promise<RadarCoverage> {
  const northWest = lonLatToTile(bounds.getWest(), bounds.getNorth(), z);
  const southEast = lonLatToTile(bounds.getEast(), bounds.getSouth(), z);
  const n = 2 ** z;
  const xValues: number[] = [];
  if (northWest.x <= southEast.x) {
    for (let x = northWest.x; x <= southEast.x; x += 1) xValues.push(x);
  } else {
    for (let x = northWest.x; x < n; x += 1) xValues.push(x);
    for (let x = 0; x <= southEast.x; x += 1) xValues.push(x);
  }
  const yValues: number[] = [];
  for (let y = Math.max(0, northWest.y); y <= Math.min(n - 1, southEast.y); y += 1) yValues.push(y);
  const tilePairs = xValues.flatMap((x) => yValues.map((y) => ({ x, y }))).slice(0, 16);
  if (!tilePairs.length) return null;

  let nonTransparent = 0;
  let totalPixels = 0;
  let maxDbz: number | undefined;
  await Promise.all(tilePairs.map(async ({ x, y }) => {
    const image = await loadRadarImage(radarTileUrl(tileUrl, z, x, y));
    if (!image) return;
    const pixels = image.data;
    for (let index = 3; index < pixels.length; index += 4) if (pixels[index] > 0) nonTransparent += 1;
    for (let index = 0; index < pixels.length; index += 64) {
      const alpha = pixels[index + 3];
      if (alpha < 145) continue;
      const dbz = nearestDbz(pixels[index], pixels[index + 1], pixels[index + 2], alpha);
      if (dbz >= 10) maxDbz = Math.max(maxDbz || 0, dbz);
    }
    totalPixels += image.width * image.height;
  }));
  return totalPixels ? { percent: (nonTransparent / totalPixels) * 100, tiles: tilePairs.length, maxDbz } : null;
}
